"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import type { Game } from "@/lib/data";
import { EASE, spring } from "@/lib/motion";
import { usePlayer } from "@/components/shell/PlayerContext";
import { useShell } from "@/components/shell/ShellContext";
import { PlatformError } from "@/lib/api/client";
import { useRealLaunch } from "@/lib/api/hooks";
import { messageFor } from "@/lib/api/messages";
import { useIdentity } from "@/lib/api/session";
import { Icon } from "@/components/ui/Icon";
import { RemoteImage } from "@/components/ui/RemoteImage";
import { Button, Chip, Tooltip } from "@/components/ui/primitives";
import { SectionEmpty, SectionError } from "@/components/ui/states";

/* The playable frame.

   The game is not loaded until the visitor asks for it: a launch URL is
   single-use, and autoplaying an embed on navigation burns it and costs the
   provider a session. So the poster stays until Play is pressed. */

export function GameStage({
  game,
  gameId,
  launchUrl,
  launchError,
  isBlocked,
  code,
  previousSlug,
  nextSlug,
}: {
  game: Game;
  gameId: string;
  launchUrl: string | null;
  launchError: string | null;
  isBlocked: boolean;
  code: string;
  previousSlug: string | null;
  nextSlug: string | null;
}) {
  const { openWallet } = useShell();
  const player = usePlayer();
  const { isSignedIn } = useIdentity();
  const realLaunch = useRealLaunch();
  const [playing, setPlaying] = useState(false);
  const [details, setDetails] = useState(false);
  const [canFullscreen, setCanFullscreen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pops, setPops] = useState(0);
  const stageRef = useRef<HTMLDivElement>(null);

  const favourite = player.isFavorite(gameId);

  useEffect(() => {
    const sync = () => setIsFullscreen(document.fullscreenElement !== null);

    setCanFullscreen(document.fullscreenEnabled);
    sync();
    document.addEventListener("fullscreenchange", sync);

    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => undefined);
      return;
    }

    void stageRef.current?.requestFullscreen().catch(() => undefined);
  };

  const playRealError = realLaunch.isError
    ? messageFor(
        realLaunch.error instanceof PlatformError
          ? realLaunch.error
          : new PlatformError(0, "network", ""),
        "games",
      )
    : null;

  const src = realLaunch.data ?? launchUrl;

  /* Recording the play here rather than on arrival is what makes "Recently
     Played" mean it: opening a game's page is not playing it. Fun and real
     both count — the list is what you played, not how you paid for it. */
  const startPlaying = () => {
    setPlaying(true);
    player.recordPlay(gameId);
  };

  return (
    <>
      <motion.div
        ref={stageRef}
        initial={{ opacity: 0, scale: 0.985 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="relative aspect-[369/668] w-full overflow-hidden rounded-t-2xl bg-elevation-1 sm:aspect-[1096/606] [&:fullscreen]:aspect-auto [&:fullscreen]:h-full [&:fullscreen]:rounded-none"
        style={playing ? undefined : { background: `linear-gradient(160deg, ${game.from}, ${game.to})` }}
      >
        {playing && src ? (
          <iframe
            src={src}
            title={game.title}
            className="absolute inset-0 h-full w-full border-0"
            allow="autoplay; fullscreen; clipboard-write"
            allowFullScreen
          />
        ) : (
          <Poster
            game={game}
            isBlocked={isBlocked}
            launchUrl={launchUrl}
            launchError={launchError ?? playRealError}
            canPlayReal={isSignedIn && !isBlocked}
            isStartingReal={realLaunch.isPending}
            onPlay={startPlaying}
            onPlayReal={() => realLaunch.mutate(gameId, { onSuccess: startPlaying })}
          />
        )}
      </motion.div>

      <div className="-mt-[16px] flex h-[78px] flex-wrap items-center gap-[8px] rounded-b-2xl bg-elevation-1 p-[16px]">
        {player.enabled && (
          <ToolButton
            name="star"
            label={favourite ? "Remove from favourites" : "Add to favourites"}
            active={favourite}
            pressed={pops}
            onClick={() => {
              setPops((count) => count + 1);
              player.toggleFavorite(gameId);
            }}
          />
        )}
        {canFullscreen && (
          <Tooltip content={isFullscreen ? "Leave fullscreen" : "Play fullscreen"}>
            <ToolButton
              name="expand"
              label={isFullscreen ? "Leave fullscreen" : "Play fullscreen"}
              active={isFullscreen}
              onClick={toggleFullscreen}
            />
          </Tooltip>
        )}
        {playing && (
          <Tooltip content="Close the game">
            <ToolButton name="close" label="Close the game" onClick={() => setPlaying(false)} />
          </Tooltip>
        )}

        <div className="mx-auto hidden items-center gap-[8px] sm:flex">
          <StepLink dir="previous" slug={previousSlug} />
          <StepLink dir="next" slug={nextSlug} />
        </div>

        <Button
          size="lg"
          variant="secondary"
          icon="wallet"
          className="ml-auto sm:ml-0"
          onClick={() => openWallet("Deposit")}
        >
          Deposit
        </Button>
      </div>

      <div className="rounded-2xl bg-elevation-1">
        <div className="flex flex-wrap items-center gap-[10px] p-[20px]">
          <h1 className="text-h6 font-bold lg:text-h5">{game.title}</h1>
          <Chip size="lg" className="uppercase">{game.studio}</Chip>
          {isBlocked ? (
            <Chip size="lg" tone="error">
              <Icon name="lock" size={12} />
              Not available here
            </Chip>
          ) : (
            <Chip size="lg" tone="highlight">Demo available</Chip>
          )}

          <button
            type="button"
            onClick={() => setDetails((value) => !value)}
            aria-expanded={details}
            aria-label="Toggle details"
            className="ml-auto grid h-[24px] w-[24px] place-items-center text-fg transition-colors hover:text-fg-muted"
          >
            <motion.span animate={{ rotate: details ? 180 : 0 }} transition={{ duration: 0.25, ease: EASE }}>
              <Icon name="chevron-down" size={24} />
            </motion.span>
          </button>
        </div>

        <motion.div
          initial={false}
          animate={{ height: details ? "auto" : 0, opacity: details ? 1 : 0 }}
          transition={{ duration: 0.3, ease: EASE }}
          className="overflow-hidden"
        >
          {/* Only fields the API actually returns. Volatility, max win and reel
              count are not exposed, so they are not shown. */}
          <div className="grid gap-[12px] border-t border-border p-[20px] sm:grid-cols-3">
            <Fact label="Studio" value={game.studio} />
            <Fact label="Game code" value={code} />
            <Fact label="Availability" value={isBlocked ? "Blocked in your region" : "Available"} />
          </div>
        </motion.div>
      </div>
    </>
  );
}

function Poster({
  game,
  isBlocked,
  launchUrl,
  launchError,
  canPlayReal,
  isStartingReal,
  onPlay,
  onPlayReal,
}: {
  game: Game;
  isBlocked: boolean;
  launchUrl: string | null;
  launchError: string | null;
  canPlayReal: boolean;
  isStartingReal: boolean;
  onPlay: () => void;
  onPlayReal: () => void;
}) {
  return (
    <>
      {game.thumbnailUrl && (
        <RemoteImage
          src={game.thumbnailUrl}
          width={330}
          height={440}
          className="scale-110 opacity-35 blur-xl"
        />
      )}
      <span className="absolute inset-0 bg-[radial-gradient(70%_60%_at_50%_18%,rgba(255,255,255,0.22),transparent_65%)]" />
      <span className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_120%,rgba(0,0,0,0.7),transparent_60%)]" />

      <div className="relative grid h-full place-items-center px-6">
        <div className="flex max-w-[46ch] flex-col items-center gap-5 text-center">
          <h2 className="text-[30px] font-black uppercase leading-[0.94] tracking-[-0.03em] text-white [text-shadow:0_4px_20px_rgba(0,0,0,0.55)] lg:text-[46px]">
            {game.title}
          </h2>

          {isBlocked ? (
            <div className="w-full max-w-[52ch] rounded-2xl bg-black/45 backdrop-blur-sm">
              <SectionEmpty
                icon="lock"
                title="Not available in your region"
                body="The licence rules that apply to your location block this game, so neither the demo nor real-money play can start here."
                className="py-8"
              />
            </div>
          ) : (
            <>
              {launchError && (
                <div className="w-full max-w-[52ch] rounded-2xl bg-black/45 backdrop-blur-sm">
                  <SectionError title="This game couldn’t be started" body={launchError} className="py-8" />
                </div>
              )}

              {!launchError && launchUrl && (
                <>
                  <motion.button
                    type="button"
                    onClick={onPlay}
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.95 }}
                    className="glow-brand grid h-20 w-20 place-items-center rounded-full bg-brand text-fg-on-primary ring-1 ring-white/25"
                    aria-label={`Play ${game.title} in demo mode`}
                  >
                    <Icon name="rocket" size={30} />
                  </motion.button>
                  <p className="text-[12px] font-bold uppercase tracking-[0.3em] text-white/80">
                    Play in demo mode
                  </p>

                  {canPlayReal && (
                    <button
                      type="button"
                      onClick={onPlayReal}
                      disabled={isStartingReal}
                      className="h-[42px] rounded-lg bg-white/15 px-[20px] text-[12px] font-bold leading-[1.6] text-white backdrop-blur transition-colors hover:bg-white/25 disabled:opacity-60"
                    >
                      {isStartingReal ? "Starting…" : "Play for real"}
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="surface-sheen-2 rounded-xl border border-border p-[24px]">
      <p className="text-[12px] font-bold uppercase leading-[1.6] tracking-[0.6px] text-fg-subtle">{label}</p>
      <p className="mt-[8px] break-words text-[15.75px] font-bold leading-[1.6] text-fg lg:text-[17.5px]">{value}</p>
    </div>
  );
}

function ToolButton({
  name,
  label,
  active,
  pressed,
  onClick,
}: {
  name: string;
  label: string;
  active?: boolean;
  pressed?: number;
  onClick?: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      aria-label={label}
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.94 }}
      className={cn(
        "grid h-[46px] w-[46px] shrink-0 place-items-center rounded-lg transition-colors duration-200",
        active ? "bg-brand text-fg-on-primary" : "bg-elevation-2 text-fg hover:bg-elevation-3",
      )}
    >
      <motion.span
        key={pressed}
        initial={pressed ? { scale: 0.4, rotate: -45 } : false}
        animate={{ scale: 1, rotate: 0 }}
        transition={spring}
        className="grid place-items-center"
      >
        <Icon name={name} size={24} />
      </motion.span>
    </motion.button>
  );
}

function StepLink({ dir, slug }: { dir: "previous" | "next"; slug: string | null }) {
  const label = dir === "previous" ? "Previous recommended game" : "Next recommended game";
  const icon = dir === "previous" ? "arrow-left" : "arrow-right";

  if (!slug) {
    return (
      <button
        type="button"
        disabled
        aria-label={label}
        className="grid h-[46px] w-[46px] shrink-0 cursor-not-allowed place-items-center rounded-lg bg-elevation-2 text-fg-disabled"
      >
        <Icon name={icon} size={24} />
      </button>
    );
  }

  return (
    <Tooltip content={label}>
      <Link
        href={`/game/${slug}`}
        aria-label={label}
        className="grid h-[46px] w-[46px] shrink-0 place-items-center rounded-lg bg-elevation-2 text-fg transition-colors duration-200 hover:bg-elevation-3"
      >
        <Icon name={icon} size={24} />
      </Link>
    </Tooltip>
  );
}
